import { useCallback, useEffect, useRef, useState } from 'react'
import {
  getBackupSettings,
  saveBackupSettings,
  runBackup,
  runRestore,
  getBackupJobStatus,
  type JobStatus,
  changePassword,
  getOcrSettings,
  saveOcrSettings,
  type OcrProvider,
  getAIKeyStatus,
  saveAIKey,
  deleteAIKey,
  saveAIProvider,
  type AISearchProvider,
} from '../api/settings'

type StatusMsg = { text: string; ok: boolean } | null

export const Settings = () => {
  const [aiKeyInput, setAiKeyInput] = useState('')
  const [aiKeyConfigured, setAiKeyConfigured] = useState<boolean | null>(null)
  const [aiProvider, setAiProvider] = useState<AISearchProvider>('anthropic')
  const [aiKeyStatus, setAiKeyStatus] = useState<StatusMsg>(null)
  const [aiKeyLoading, setAiKeyLoading] = useState(false)
  const [gitUrl, setGitUrl] = useState('')
  const [publicKey, setPublicKey] = useState('')
  const [publicKeyLoading, setPublicKeyLoading] = useState(true)
  const [publicKeyError, setPublicKeyError] = useState('')
  const [copied, setCopied] = useState(false)
  const [backupSettingsStatus, setBackupSettingsStatus] = useState<StatusMsg>(null)
  const [backupStatus, setBackupStatus] = useState<StatusMsg>(null)
  const [restoreStatus, setRestoreStatus] = useState<StatusMsg>(null)
  const [backupRunning, setBackupRunning] = useState(false)
  const [restoreRunning, setRestoreRunning] = useState(false)

  const [ocrProvider, setOcrProvider] = useState<OcrProvider>('tesseract')
  const [gcvKey, setGcvKey] = useState('')
  const [mistralKey, setMistralKey] = useState('')
  const [gcvKeySet, setGcvKeySet] = useState(false)
  const [mistralKeySet, setMistralKeySet] = useState(false)
  const [ocrStatus, setOcrStatus] = useState<StatusMsg>(null)

  const [currentPw, setCurrentPw] = useState('')
  const [newPw, setNewPw] = useState('')
  const [confirmPw, setConfirmPw] = useState('')
  const [pwStatus, setPwStatus] = useState<StatusMsg>(null)
  const [pwLoading, setPwLoading] = useState(false)

  useEffect(() => {
    getOcrSettings()
      .then((res) => {
        setOcrProvider(res.data.provider as OcrProvider)
        setGcvKeySet(res.data.google_cloud_vision_api_key_set)
        setMistralKeySet(res.data.mistral_api_key_set)
      })
      .catch(() => {})
  }, [])

  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null)

  const applyJobStatus = useCallback((s: JobStatus) => {
    const msg = s.state === 'idle' ? null : { text: s.message, ok: s.state !== 'error' }
    if (s.kind === 'restore') {
      setRestoreRunning(s.state === 'running')
      setRestoreStatus(msg)
    } else {
      setBackupRunning(s.state === 'running')
      setBackupStatus(msg)
    }
  }, [])

  // Backup/Restore laufen im Backend weiter, unabhängig von dieser Seite. Der
  // Fortschritt wird deshalb abgefragt statt auf die Antwort des POST zu warten.
  // useCallback hält die Funktion stabil, damit der Effekt unten beim Mounten
  // einmal läuft und nicht bei jedem Render ein neues Polling aufsetzt.
  const trackJob = useCallback((s: JobStatus) => {
    applyJobStatus(s)
    if (s.state !== 'running' || pollRef.current) return
    pollRef.current = setInterval(() => {
      getBackupJobStatus()
        .then((res) => {
          applyJobStatus(res.data)
          if (res.data.state !== 'running' && pollRef.current) {
            clearInterval(pollRef.current)
            pollRef.current = null
          }
        })
        .catch(() => {
          // Vorübergehender Netzwerkfehler: beim nächsten Intervall neu versuchen.
        })
    }, 2000)
  }, [applyJobStatus])

  useEffect(() => {
    // Laufenden oder zuletzt gelaufenen Job übernehmen, damit ein Reload der
    // Seite den Stand nicht verliert.
    getBackupJobStatus()
      .then((res) => trackJob(res.data))
      .catch(() => {})
    return () => {
      if (pollRef.current) clearInterval(pollRef.current)
    }
  }, [trackJob])

  useEffect(() => {
    getBackupSettings()
      .then((res) => {
        setGitUrl(res.data.git_url ?? '')
        setPublicKey(res.data.public_key ?? '')
      })
      .catch((err) => {
        const msg = err?.response?.data?.detail ?? 'Failed to load backup settings.'
        setPublicKeyError(msg)
      })
      .finally(() => setPublicKeyLoading(false))

    getAIKeyStatus()
      .then((res) => {
        setAiKeyConfigured(res.data.configured)
        setAiProvider(res.data.provider)
      })
      .catch(() => setAiKeyConfigured(false))
  }, [])

  const handleSaveOcrSettings = () => {
    setOcrStatus(null)
    const payload: { provider: OcrProvider; google_cloud_vision_api_key?: string; mistral_api_key?: string } = { provider: ocrProvider }
    if (ocrProvider === 'google_cloud_vision' && gcvKey) payload.google_cloud_vision_api_key = gcvKey
    if (ocrProvider === 'mistral' && mistralKey) payload.mistral_api_key = mistralKey
    saveOcrSettings(payload)
      .then((res) => {
        setGcvKeySet(res.data.google_cloud_vision_api_key_set)
        setMistralKeySet(res.data.mistral_api_key_set)
        setGcvKey('')
        setMistralKey('')
        setOcrStatus({ text: 'Einstellungen gespeichert.', ok: true })
      })
      .catch((err) => {
        const msg = err?.response?.data?.detail ?? 'Fehler beim Speichern.'
        setOcrStatus({ text: msg, ok: false })
      })
  }

  const handleSaveAIProvider = (provider: AISearchProvider) => {
    setAiProvider(provider)
    saveAIProvider(provider)
      .then((res) => setAiKeyConfigured(res.data.configured))
      .catch(() => {})
  }

  const handleSaveAIKey = () => {
    if (!aiKeyInput.trim()) return
    setAiKeyStatus(null)
    setAiKeyLoading(true)
    saveAIKey(aiKeyInput.trim())
      .then(() => {
        setAiKeyConfigured(true)
        setAiKeyInput('')
        setAiKeyStatus({ text: 'API-Key gespeichert.', ok: true })
      })
      .catch((err) => {
        const msg = err?.response?.data?.detail ?? 'Fehler beim Speichern.'
        setAiKeyStatus({ text: msg, ok: false })
      })
      .finally(() => setAiKeyLoading(false))
  }

  const handleDeleteAIKey = () => {
    setAiKeyStatus(null)
    setAiKeyLoading(true)
    deleteAIKey()
      .then(() => {
        setAiKeyConfigured(false)
        setAiKeyStatus({ text: 'API-Key entfernt.', ok: true })
      })
      .catch((err) => {
        const msg = err?.response?.data?.detail ?? 'Fehler beim Entfernen.'
        setAiKeyStatus({ text: msg, ok: false })
      })
      .finally(() => setAiKeyLoading(false))
  }

  const handleSaveBackupSettings = () => {
    setBackupSettingsStatus(null)
    saveBackupSettings({ git_url: gitUrl })
      .then((res) => {
        setPublicKey(res.data.public_key ?? '')
        setBackupSettingsStatus({ text: 'Settings saved.', ok: true })
      })
      .catch((err) => {
        const msg = err?.response?.data?.detail ?? 'Failed to save settings.'
        setBackupSettingsStatus({ text: msg, ok: false })
      })
  }

  const handleCopyKey = () => {
    navigator.clipboard.writeText(publicKey).then(() => {
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    })
  }

  const handleBackup = () => {
    setBackupStatus(null)
    setBackupRunning(true)
    runBackup()
      .then((res) => trackJob(res.data))
      .catch((err) => {
        setBackupRunning(false)
        const msg = err?.response?.data?.detail ?? 'Backup request failed.'
        setBackupStatus({ text: msg, ok: false })
      })
  }

  const handleRestore = () => {
    if (!confirm('This will overwrite your current data with the backup. Continue?')) return
    setRestoreStatus(null)
    setRestoreRunning(true)
    runRestore()
      .then((res) => trackJob(res.data))
      .catch((err) => {
        setRestoreRunning(false)
        const msg = err?.response?.data?.detail ?? 'Restore request failed.'
        setRestoreStatus({ text: msg, ok: false })
      })
      .finally(() => setRestoreRunning(false))
  }

  const handleChangePassword = () => {
    setPwStatus(null)
    if (newPw !== confirmPw) {
      setPwStatus({ text: 'New passwords do not match.', ok: false })
      return
    }
    if (newPw.length < 6) {
      setPwStatus({ text: 'New password must be at least 6 characters.', ok: false })
      return
    }
    setPwLoading(true)
    changePassword({ current_password: currentPw, new_password: newPw })
      .then(() => {
        setPwStatus({ text: 'Password changed successfully.', ok: true })
        setCurrentPw('')
        setNewPw('')
        setConfirmPw('')
      })
      .catch((err) => {
        const msg = err?.response?.data?.detail ?? 'Failed to change password.'
        setPwStatus({ text: msg, ok: false })
      })
      .finally(() => setPwLoading(false))
  }

  return (
    <div style={{ maxWidth: '640px' }}>
      <h2>Einstellungen</h2>

      {/* Git Backup Section */}
      <section style={{ marginBottom: '2.5rem' }}>
        <h3 style={{ borderBottom: '1px solid #ddd', paddingBottom: '0.4rem' }}>
          Git Backup
        </h3>
        <p style={{ color: '#666', fontSize: '0.9rem', marginBottom: '1rem' }}>
          Configure a git repository to backup your database and images. Use an SSH
          URL (e.g. <code>git@github.com:you/backup.git</code>).
        </p>

        <div style={{ marginBottom: '1rem' }}>
          <label style={{ display: 'block', marginBottom: '0.3rem', fontWeight: 500 }}>
            SSH Public Key
            <span style={{ color: '#666', fontWeight: 400, marginLeft: '0.5rem', fontSize: '0.85rem' }}>
              — add this as a deploy key to your repository
            </span>
          </label>
          {publicKeyError ? (
            <p style={{ color: '#c0392b', fontSize: '0.9rem' }}>{publicKeyError}</p>
          ) : publicKey ? (
            <>
              <textarea
                readOnly
                value={publicKey}
                rows={3}
                style={{
                  width: '100%',
                  padding: '0.4rem 0.6rem',
                  fontFamily: 'monospace',
                  fontSize: '0.8rem',
                  resize: 'none',
                  boxSizing: 'border-box',
                  background: '#f5f5f5',
                }}
              />
              <button onClick={handleCopyKey} className="btn btn-outline" style={{ marginTop: '0.25rem' }}>
                {copied ? 'Copied!' : 'Copy public key'}
              </button>
            </>
          ) : publicKeyLoading ? (
            <p style={{ color: '#888', fontSize: '0.9rem' }}>Generating key…</p>
          ) : (
            <p style={{ color: '#c0392b', fontSize: '0.9rem' }}>No key returned from server.</p>
          )}
        </div>

        <div style={{ marginBottom: '1rem', marginTop: '1.5rem' }}>
          <label style={{ display: 'block', marginBottom: '0.3rem', fontWeight: 500 }}>
            Git Repository URL
          </label>
          <input
            type="text"
            value={gitUrl}
            onChange={(e) => setGitUrl(e.target.value)}
            placeholder="git@github.com:you/marginalia-backup.git"
            style={{ width: '100%', padding: '0.4rem 0.6rem', fontFamily: 'monospace', boxSizing: 'border-box' }}
          />
        </div>

        <button onClick={handleSaveBackupSettings} className="btn btn-primary">
          Save Settings
        </button>

        {backupSettingsStatus && (
          <p style={{ marginTop: '0.5rem', color: backupSettingsStatus.ok ? '#4a7c59' : '#c0392b' }}>
            {backupSettingsStatus.text}
          </p>
        )}

        <div style={{ marginTop: '1.5rem', display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
          {/* Backup und Restore teilen sich einen Job-Slot im Backend, ein
              zweiter Start würde mit 409 abgelehnt. */}
          <button
            onClick={handleBackup}
            disabled={backupRunning || restoreRunning}
            className="btn btn-primary"
          >
            {backupRunning ? 'Backing up…' : 'Backup now'}
          </button>
          <button
            onClick={handleRestore}
            disabled={backupRunning || restoreRunning}
            className="btn btn-danger"
          >
            {restoreRunning ? 'Restoring…' : 'Restore from Git'}
          </button>
        </div>

        {backupStatus && (
          <p style={{ marginTop: '0.5rem', color: backupStatus.ok ? '#4a7c59' : '#c0392b' }}>
            {backupStatus.text}
          </p>
        )}
        {restoreStatus && (
          <p style={{ marginTop: '0.5rem', color: restoreStatus.ok ? '#4a7c59' : '#c0392b' }}>
            {restoreStatus.text}
          </p>
        )}
      </section>

      {/* OCR Section */}
      <section style={{ marginBottom: '2.5rem' }}>
        <h3 style={{ borderBottom: '1px solid #ddd', paddingBottom: '0.4rem' }}>
          OCR-Dienst
        </h3>
        <p style={{ color: '#666', fontSize: '0.9rem', marginBottom: '1rem' }}>
          Wähle den Dienst zur Texterkennung. Tesseract läuft lokal (keine API-Schlüssel nötig),
          Google Cloud Vision und Mistral OCR 3 benötigen einen API-Schlüssel.
        </p>

        <div style={{ marginBottom: '1rem' }}>
          <label style={{ display: 'block', marginBottom: '0.3rem', fontWeight: 500 }}>
            Anbieter
          </label>
          <select
            value={ocrProvider}
            onChange={(e) => setOcrProvider(e.target.value as OcrProvider)}
            style={{ width: '100%', padding: '0.4rem 0.6rem', boxSizing: 'border-box' }}
          >
            <option value="tesseract">Tesseract (lokal, Standard)</option>
            <option value="google_cloud_vision">Google Cloud Vision</option>
            <option value="mistral">Mistral OCR 3</option>
          </select>
        </div>

        {ocrProvider === 'google_cloud_vision' && (
          <div style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'block', marginBottom: '0.3rem', fontWeight: 500 }}>
              Google Cloud Vision API-Schlüssel
              {gcvKeySet && (
                <span style={{ color: '#4a7c59', fontWeight: 400, marginLeft: '0.5rem', fontSize: '0.85rem' }}>
                  — bereits gesetzt
                </span>
              )}
            </label>
            <input
              type="password"
              value={gcvKey}
              onChange={(e) => setGcvKey(e.target.value)}
              placeholder={gcvKeySet ? 'Neuen Schlüssel eingeben zum Ändern' : 'API-Schlüssel eingeben'}
              style={{ width: '100%', padding: '0.4rem 0.6rem', boxSizing: 'border-box' }}
            />
          </div>
        )}

        {ocrProvider === 'mistral' && (
          <div style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'block', marginBottom: '0.3rem', fontWeight: 500 }}>
              Mistral API-Schlüssel
              {mistralKeySet && (
                <span style={{ color: '#4a7c59', fontWeight: 400, marginLeft: '0.5rem', fontSize: '0.85rem' }}>
                  — bereits gesetzt
                </span>
              )}
            </label>
            <input
              type="password"
              value={mistralKey}
              onChange={(e) => setMistralKey(e.target.value)}
              placeholder={mistralKeySet ? 'Neuen Schlüssel eingeben zum Ändern' : 'API-Schlüssel eingeben'}
              style={{ width: '100%', padding: '0.4rem 0.6rem', boxSizing: 'border-box' }}
            />
          </div>
        )}

        <button onClick={handleSaveOcrSettings} className="btn btn-primary">
          Speichern
        </button>

        {ocrStatus && (
          <p style={{ marginTop: '0.5rem', color: ocrStatus.ok ? '#4a7c59' : '#c0392b' }}>
            {ocrStatus.text}
          </p>
        )}
      </section>

      {/* AI Search Section */}
      <section style={{ marginBottom: '2.5rem' }}>
        <h3 style={{ borderBottom: '1px solid #ddd', paddingBottom: '0.4rem' }}>
          KI-Suche
        </h3>
        <p style={{ color: '#666', fontSize: '0.9rem', marginBottom: '1rem' }}>
          Wähle den Anbieter für die KI-gestützte Themensuche.
        </p>

        <div style={{ marginBottom: '1rem' }}>
          <label style={{ display: 'block', marginBottom: '0.3rem', fontWeight: 500 }}>
            Anbieter
          </label>
          <select
            value={aiProvider}
            onChange={(e) => handleSaveAIProvider(e.target.value as AISearchProvider)}
            style={{ width: '100%', padding: '0.4rem 0.6rem', boxSizing: 'border-box' }}
          >
            <option value="anthropic">Anthropic Claude (Haiku)</option>
            <option value="mistral">Mistral (mistral-small-latest)</option>
          </select>
        </div>

        {aiProvider === 'anthropic' && (
          <>
            <p style={{ color: '#666', fontSize: '0.9rem', marginBottom: '0.75rem' }}>
              Benötigt einen eigenen{' '}
              <a href="https://console.anthropic.com/settings/keys" target="_blank" rel="noreferrer">
                Anthropic API-Key
              </a>
              . Der Key wird verschlüsselt in deiner persönlichen Datenbank gespeichert.
            </p>
            {aiKeyConfigured && (
              <p style={{ color: '#4a7c59', fontSize: '0.9rem', marginBottom: '0.75rem' }}>
                ✓ API-Key ist hinterlegt.
              </p>
            )}
            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap', marginBottom: '0.5rem' }}>
              <input
                type="password"
                placeholder={aiKeyConfigured ? 'Neuen Key eingeben um zu ersetzen…' : 'sk-ant-…'}
                value={aiKeyInput}
                onChange={(e) => setAiKeyInput(e.target.value)}
                style={{ padding: '0.4rem 0.6rem', fontFamily: 'monospace', width: '320px', boxSizing: 'border-box' }}
              />
              <button
                onClick={handleSaveAIKey}
                disabled={aiKeyLoading || !aiKeyInput.trim()}
                className="login-button"
              >
                {aiKeyLoading ? 'Speichert…' : 'Speichern'}
              </button>
              {aiKeyConfigured && (
                <button
                  onClick={handleDeleteAIKey}
                  disabled={aiKeyLoading}
                  className="logout-button"
                >
                  Entfernen
                </button>
              )}
            </div>
          </>
        )}

        {aiProvider === 'mistral' && (
          <>
            <p style={{ color: '#666', fontSize: '0.9rem', marginBottom: '0.75rem' }}>
              Benötigt einen{' '}
              <a href="https://console.mistral.ai/api-keys" target="_blank" rel="noreferrer">
                Mistral API-Key
              </a>
              . Derselbe Key wird auch für Mistral OCR verwendet.
            </p>
            {aiKeyConfigured && (
              <p style={{ color: '#4a7c59', fontSize: '0.9rem', marginBottom: '0.75rem' }}>
                ✓ Mistral API-Key ist hinterlegt.
              </p>
            )}
            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap', marginBottom: '0.5rem' }}>
              <input
                type="password"
                placeholder={aiKeyConfigured ? 'Neuen Key eingeben um zu ersetzen…' : 'API-Schlüssel eingeben'}
                value={aiKeyInput}
                onChange={(e) => setAiKeyInput(e.target.value)}
                style={{ padding: '0.4rem 0.6rem', fontFamily: 'monospace', width: '320px', boxSizing: 'border-box' }}
              />
              <button
                onClick={handleSaveAIKey}
                disabled={aiKeyLoading || !aiKeyInput.trim()}
                className="login-button"
              >
                {aiKeyLoading ? 'Speichert…' : 'Speichern'}
              </button>
              {aiKeyConfigured && (
                <button
                  onClick={handleDeleteAIKey}
                  disabled={aiKeyLoading}
                  className="logout-button"
                >
                  Entfernen
                </button>
              )}
            </div>
          </>
        )}

        {aiKeyStatus && (
          <p style={{ color: aiKeyStatus.ok ? '#4a7c59' : '#c0392b', fontSize: '0.9rem' }}>
            {aiKeyStatus.text}
          </p>
        )}
      </section>

      {/* Change Password Section */}
      <section>
        <h3 style={{ borderBottom: '1px solid #ddd', paddingBottom: '0.4rem' }}>
          Passwort ändern
        </h3>

        <div style={{ marginBottom: '0.75rem' }}>
          <label style={{ display: 'block', marginBottom: '0.3rem', fontWeight: 500 }}>
            Aktuelles Passwort
          </label>
          <input
            type="password"
            value={currentPw}
            onChange={(e) => setCurrentPw(e.target.value)}
            style={{ width: '100%', padding: '0.4rem 0.6rem', boxSizing: 'border-box' }}
          />
        </div>

        <div style={{ marginBottom: '0.75rem' }}>
          <label style={{ display: 'block', marginBottom: '0.3rem', fontWeight: 500 }}>
            Neues Passwort
          </label>
          <input
            type="password"
            value={newPw}
            onChange={(e) => setNewPw(e.target.value)}
            style={{ width: '100%', padding: '0.4rem 0.6rem', boxSizing: 'border-box' }}
          />
        </div>

        <div style={{ marginBottom: '1rem' }}>
          <label style={{ display: 'block', marginBottom: '0.3rem', fontWeight: 500 }}>
            Neues Passwort bestätigen
          </label>
          <input
            type="password"
            value={confirmPw}
            onChange={(e) => setConfirmPw(e.target.value)}
            style={{ width: '100%', padding: '0.4rem 0.6rem', boxSizing: 'border-box' }}
          />
        </div>

        <button
          onClick={handleChangePassword}
          disabled={pwLoading}
          className="btn btn-primary"
        >
          {pwLoading ? 'Wird geändert…' : 'Passwort ändern'}
        </button>

        {pwStatus && (
          <p style={{ marginTop: '0.5rem', color: pwStatus.ok ? '#4a7c59' : '#c0392b' }}>
            {pwStatus.text}
          </p>
        )}
      </section>
    </div>
  )
}
